import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { previewClassRule, publishClassRule, updateClassRule } from '../services/classAuditApi';
import { validateRuleDraft } from '../utils/ruleCsvRow';
import { levelOptionsForProgram, levelOptionLabel } from '../utils/levelOptions';

// Create / edit a REUSABLE rule against the app-owned rule table. Used from two places:
//   * Class Audit's Missing Levels panel -> create, seeded from a discovered candidate group;
//   * Rule Management -> create from scratch, or edit an existing rule.
// The preview is production-equivalent (run in the warehouse via /class-rule-preview) and the server
// RE-RUNS it before committing, so what the user sees is what gets validated. A rule saved here is
// authoritative in the app table immediately; Class Audit reflects it only after the next dbt build.

const SAFETY = {
  safe: { color: 'success', label: 'Safe — no conflicts' },
  warning: { color: 'warning', label: 'Warning — existing mappings would change' },
  blocked: { color: 'error', label: 'Blocked — same-rank level conflict' },
};

export default function RuleEditorDialog({ open, onClose, mode = 'create', group = null, rule = null, levels, trackingyear, onSaved }) {
  const isEdit = mode === 'edit' && rule;

  const [pattern, setPattern] = useState('');
  const [levelId, setLevelId] = useState('');
  const [priority, setPriority] = useState(1);
  const [applyGlobal, setApplyGlobal] = useState(true);
  const [comCodeInput, setComCodeInput] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmWarning, setConfirmWarning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const singleFranchise = group && group.franchiseCount === 1 ? (group.representativeFranchises || [])[0] : null;

  // (Re)initialise whenever a new target opens.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setPattern(rule.team_name_pattern || '');
      setLevelId(rule.level_id ?? '');
      setPriority(rule.priority ?? 1);
      setApplyGlobal(rule.com_code === '*');
      setComCodeInput(rule.com_code === '*' ? '' : (rule.com_code || ''));
    } else {
      setPattern(group ? (group.stem || '') : '');
      setLevelId('');
      setPriority(1);
      setApplyGlobal(singleFranchise ? false : true);
      setComCodeInput(singleFranchise || '');
    }
    setPreview(null); setConfirmWarning(false); setSaved(null); setErrorMsg('');
  }, [open, mode, group, rule, isEdit, singleFranchise]);

  const comCode = applyGlobal ? '*' : (comCodeInput.trim() || '');

  // Levels offered as a RULE TARGET:
  //   * program type ORDERS, it no longer filters: the seed's program first (the class's season, or
  //     the edited rule's level), then the other program's levels, labelled. A rule matches class
  //     names, and an SI class in an LK season still needs an SI level (LKFD-201). See
  //     levelOptionsForProgram;
  //   * is_rule_targetable -- 'LK Other' / 'SI Other' record a per-class human judgement that a
  //     class does not belong to a normal instructional level, which is the opposite of a pattern
  //     generalisation. They stay selectable for manual assignment in Class Audit; they are not
  //     offered here. Hiding them is a courtesy, NOT the guard: the server refuses them in rule
  //     preview/publish/update, so a hand-crafted request is rejected too.
  //   Compared against 0 explicitly so a payload predating the column still offers every level.
  const availableLevels = useMemo(() => {
    const all = (levels || []).filter((l) => l.is_rule_targetable !== 0);
    const pt = (group && group.programType) || (rule && rule.program_type) || null;
    return levelOptionsForProgram(all, pt);
  }, [levels, group, rule]);

  const draft = { com_code: comCode, team_name_pattern: pattern, level_id: levelId, priority };
  const draftErrors = validateRuleDraft(draft);
  const draftValid = draftErrors.length === 0;

  // Any edit invalidates a completed preview: it must be re-run against the current draft before Save.
  const invalidate = () => { setPreview(null); setConfirmWarning(false); setSaved(null); };

  if (!open) return null;

  const levelItems = availableLevels.map((l) => (
    <MenuItem key={l.level_id} value={l.level_id}>{levelOptionLabel(l)}</MenuItem>
  ));

  const runPreview = () => {
    setPreviewing(true); setErrorMsg(''); setSaved(null);
    previewClassRule({
      team_name_pattern: pattern.trim(), level_id: levelId, priority, com_code: comCode,
      trackingyear, ...(isEdit ? { excludeRuleId: rule.rule_id } : {}),
    })
      .then((res) => { setPreview(res); setPreviewing(false); })
      .catch((err) => { setErrorMsg(err.message || 'Preview failed. Please retry.'); setPreviewing(false); });
  };

  // A preview that scanned zero classes means the pattern matches nothing (commonly the raw grouping
  // stem). It is NOT publishable — never present it as "safe", and disable Save until the pattern is
  // fixed and re-previewed (the server enforces the same rule, but we catch it here first).
  const noMatch = Boolean(preview) && preview.counts && preview.counts.classes_scanned === 0;
  const needsConfirm = preview && !noMatch && preview.safety === 'warning';
  const canSave = Boolean(preview) && !noMatch && preview.safety !== 'blocked' && draftValid && !saving
    && (!needsConfirm || confirmWarning);

  const handleSave = () => {
    if (!canSave) return;
    setSaving(true); setErrorMsg('');
    const payload = { team_name_pattern: pattern.trim(), level_id: levelId, priority, com_code: comCode, trackingyear };
    const call = isEdit ? updateClassRule({ rule_id: rule.rule_id, ...payload }) : publishClassRule(payload);
    call
      .then((res) => { setSaved(res); setSaving(false); if (onSaved) onSaved(res); })
      .catch((err) => { setErrorMsg(err.message || 'Save failed. Please retry.'); setSaving(false); });
  };

  // Evidence line, assembled from whichever facts the seed actually carries. A single-class seed
  // (Class Audit row) has no measured population, so it omits `occurrences` -- rendering it anyway
  // would print either "undefined" or "1 class in view", and the latter reads as "this is a one-off"
  // when the truth is simply that nothing was counted. Output is unchanged for a measured group.
  const groupFacts = [];
  if (group) {
    if (group.occurrences != null) groupFacts.push(`${group.occurrences} class${group.occurrences === 1 ? '' : 'es'} in view`);
    if (group.variantCount > 1) groupFacts.push(`${group.variantCount} name variants`);
    if (group.programType) groupFacts.push(group.programType);
    if (group.sampleNames && group.sampleNames.length) groupFacts.push(`e.g. ${group.sampleNames[0]}`);
  }

  const c = preview ? preview.counts : null;
  const safetyMeta = preview ? SAFETY[preview.safety] : null;
  const savedCounts = saved ? saved.preview && saved.preview.counts : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {isEdit ? 'Edit reusable rule' : 'Create reusable rule'}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          A reusable rule matches <b>multiple</b> classes. To change one class only, use the Class Audit
          override on that class instead.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {saved ? (
          // ---- success / pending panel ----
          <Box>
            <Alert severity="success" sx={{ mb: 1 }}>Rule saved successfully.</Alert>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Preview recomputed and validated at save time. Projected impact:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              +{savedCounts ? savedCounts.newly_mapped : 0} newly mapped
              {' · '}{savedCounts ? savedCounts.suggestion_changed : 0} changed suggestion(s)
              {' · '}{savedCounts ? savedCounts.conflicts : 0} conflict(s)
            </Typography>
            <Chip size="small" color="warning" variant="outlined" label="Pending warehouse rebuild" sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              The rule is authoritative in Season Manager now. Class Audit will continue showing the previous
              warehouse state until the next dbt build consumes it.
            </Typography>
          </Box>
        ) : (
          <Box>
            {groupFacts.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {groupFacts.join(' · ')}
              </Typography>
            )}

            <TextField
              fullWidth size="small" label="Pattern (team_name_pattern)" value={pattern}
              onChange={(e) => { setPattern(e.target.value); invalidate(); }}
              helperText="Matched with production SQL LIKE semantics (case-insensitive substring) — NOT the grouping algorithm."
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="rule-level">Canonical level (required)</InputLabel>
              <Select labelId="rule-level" label="Canonical level (required)" value={levelId}
                onChange={(e) => { setLevelId(e.target.value); invalidate(); }}>
                {levelItems}
              </Select>
            </FormControl>

            {/* The precedence guidance is a SIBLING caption, not the field's helperText: MUI sizes
                helperText to the FormControl, so the 200px input width would wrap this three-clause
                sentence into a narrow column beside an empty dialog. Same treatment the scope line
                below already uses. */}
            <TextField
              size="small" type="number" label="Priority" value={priority}
              onChange={(e) => { setPriority(e.target.value); invalidate(); }}
              inputProps={{ min: 1, step: 1 }}
              sx={{ width: 200 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
              Lower = higher precedence; then a franchise-scoped rule beats an all-franchises rule; then the longer pattern. Convention: 1.
            </Typography>

            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={applyGlobal}
                  onChange={(e) => { setApplyGlobal(e.target.checked); invalidate(); }} />}
                label="Apply to all franchises (*)"
              />
              {!applyGlobal && (
                <TextField size="small" label="Franchise (com_code)" value={comCodeInput}
                  onChange={(e) => { setComCodeInput(e.target.value); invalidate(); }}
                  helperText="The com_code this rule is scoped to." sx={{ display: 'block', maxWidth: 320, mt: 0.5 }} />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Scope: <b>{comCode || '(none)'}</b>
              </Typography>
            </Box>

            {!draftValid && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>{draftErrors.join(' ')}</Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button variant="outlined" size="small" disabled={!draftValid || previewing} onClick={runPreview}>
                {preview ? 'Re-run preview' : 'Run preview'}
              </Button>
              {previewing && <CircularProgress size={18} />}
              {!preview && !previewing && (
                <Typography variant="caption" color="text.secondary">Preview runs the production matcher in the warehouse.</Typography>
              )}
            </Box>

            {errorMsg && <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>}

            {preview && noMatch && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This pattern matches no classes. Adjust the pattern and run preview again.
              </Alert>
            )}
            {preview && !noMatch && (
              <Box sx={{ mt: 1 }}>
                <Chip size="small" color={safetyMeta.color} label={safetyMeta.label} sx={{ mb: 1 }} />
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  +{c.newly_mapped} newly mapped ({c.newly_mapped_distinct_names} distinct names)
                  {' · '}{c.suggestion_changed} changed
                  {' · '}{c.effective_changed} effective changed
                  {' · '}{c.conflicts} conflict{c.conflicts === 1 ? '' : 's'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Scanned {c.classes_scanned} matching class{c.classes_scanned === 1 ? '' : 'es'}.
                  {c.shadowed_by_override > 0 ? ` ${c.shadowed_by_override} shadowed by a manual override.` : ''}
                </Typography>
                {preview.samples && preview.samples.newly_mapped && preview.samples.newly_mapped.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    e.g. {preview.samples.newly_mapped.slice(0, 3).map((s) => s.team_name).join('  ·  ')}
                  </Typography>
                )}
                {preview.truncated && (
                  <Alert severity="warning" sx={{ mt: 1 }}>Matches exceed the scan cap — counts are lower bounds. Narrow the pattern.</Alert>
                )}
                {preview.safety === 'blocked' && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Ties an existing rule at the same rank with a different level for {c.conflicts} class(es).
                    Production resolves this deterministically by rule id, but that is a technical tie-break with
                    no business meaning — so the result is treated as an unresolved ambiguity and fails the
                    nightly warehouse integrity check. Adjust the priority or scope, or assign those classes
                    manually.
                  </Alert>
                )}
                {needsConfirm && (
                  <FormControlLabel sx={{ mt: 0.5 }}
                    control={<Checkbox size="small" checked={confirmWarning} onChange={(e) => setConfirmWarning(e.target.checked)} />}
                    label={`I understand this changes ${c.suggestion_changed} existing suggestion(s).`} />
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>{saved ? 'Close' : 'Cancel'}</Button>
        {!saved && (
          <Button variant="contained" disabled={!canSave} onClick={handleSave}>
            {saving ? <CircularProgress size={18} /> : (isEdit ? 'Save changes' : 'Publish rule')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
